import { APP_NAME } from '@circa/shared/config';
import type { ServerStatus } from '@circa/shared/protocol';
import { Backspace } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import { isBundled } from '@/core/platform';
import { createAccount, signIn, signOut, unlock, unlockWithPin, useSession } from '@/core/session';
import { Button, Field, Input, Segmented, Toggle } from '@/ui/components';
import { confirmDialog } from '@/ui/overlays';

// First run (create an account or sign in) and the lock screen.

async function fetchStatus(serverUrl: string): Promise<ServerStatus | null> {
  try {
    const res = await fetch(`${serverUrl.replace(/\/+$/, '')}/api/auth/status`);
    if (!res.ok) return null;
    return (await res.json()) as ServerStatus;
  } catch {
    return null;
  }
}

export function Welcome({ notice }: { notice?: string } = {}) {
  const [mode, setMode] = useState<'create' | 'signin'>(notice ? 'signin' : 'create');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [serverUrl, setServerUrl] = useState(
    () => localStorage.getItem('ember.lastServer') ?? import.meta.env.VITE_SERVER ?? '',
  );
  const [setupToken, setSetupToken] = useState('');
  const [sync, setSync] = useState(true);
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<ServerStatus | null | 'checking'>('checking');

  // Web builds are served by their own server; the apps need its address.
  const effectiveServer = isBundled ? serverUrl.trim() : '';
  useEffect(() => {
    if (isBundled && !serverUrl.trim()) {
      setStatus(null);
      return;
    }
    setStatus('checking');
    const t = setTimeout(() => void fetchStatus(effectiveServer).then(setStatus), 300);
    return () => clearTimeout(t);
  }, [effectiveServer, serverUrl]);

  const serverOk = status !== null && status !== 'checking';
  const needsToken = serverOk && !(status as ServerStatus).registrationOpen;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!email.trim()) return setError('Enter an email (it is your account name).');
    if (password.length < 8) return setError('Use at least 8 characters for the password.');
    if (mode === 'create' && password !== password2) return setError("Passwords don't match.");
    setBusy(true);
    try {
      if (isBundled && effectiveServer) localStorage.setItem('ember.lastServer', effectiveServer);
      if (mode === 'create') {
        await createAccount({
          email,
          password,
          serverUrl: effectiveServer,
          connect: sync && serverOk,
          setupToken: setupToken.trim() || undefined,
          remember,
        });
      } else {
        await signIn({ serverUrl: effectiveServer, email, password, remember });
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="gate">
      <form className="gate__card" onSubmit={submit}>
        <div className="gate__head">
          <div className="gate__logo" />
          <div className="gate__title">{APP_NAME}</div>
        </div>
        <div className="gate__sub">
          Sleep, food, weight and activity in one place. Private by design.
        </div>
        {notice && <div className="gate__note">{notice}</div>}
        <Segmented
          className="full"
          value={mode}
          onChange={(v) => {
            setMode(v);
            setError(null);
          }}
          options={[
            { value: 'create', label: 'Create account' },
            { value: 'signin', label: 'Sign in' },
          ]}
        />
        {isBundled && (
          <Field
            label="Server"
            hint={
              status === 'checking'
                ? 'Checking…'
                : serverOk
                  ? 'Connected'
                  : serverUrl
                    ? "Can't reach this server"
                    : 'Your server address, e.g. https://circa.example.com'
            }
          >
            <Input
              type="url"
              inputMode="url"
              autoCapitalize="off"
              placeholder="https://"
              value={serverUrl}
              onChange={(e) => setServerUrl(e.target.value)}
            />
          </Field>
        )}
        <Field label="Email">
          <Input
            type="email"
            autoComplete="username"
            autoCapitalize="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
        <div className={mode === 'create' ? 'gate__pair' : undefined}>
          <Field label="Password">
            <Input
              type="password"
              autoComplete={mode === 'create' ? 'new-password' : 'current-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
          {mode === 'create' && (
            <Field label="Confirm">
              <Input
                type="password"
                autoComplete="new-password"
                value={password2}
                onChange={(e) => setPassword2(e.target.value)}
              />
            </Field>
          )}
        </div>
        {mode === 'create' && sync && needsToken && (
          <Field label="Setup code">
            <Input
              placeholder="This server has an account; enter its setup code"
              value={setupToken}
              onChange={(e) => setSetupToken(e.target.value)}
              autoCapitalize="off"
            />
          </Field>
        )}
        <div className="gate__toggles">
          {mode === 'create' && (
            <label className="gate__toggle">
              <Toggle
                checked={sync && serverOk}
                onChange={setSync}
                disabled={!serverOk}
                label="Sync"
              />
              <span>
                {status === 'checking'
                  ? 'Checking server…'
                  : serverOk
                    ? 'Sync across devices'
                    : 'No server: this device only'}
              </span>
            </label>
          )}
          <label className="gate__toggle">
            <Toggle checked={remember} onChange={setRemember} label="Remember" />
            <span>Keep unlocked</span>
          </label>
        </div>
        {mode === 'create' && (
          <div className="gate__note">
            Everything is encrypted with your password before it leaves this device. It can't be
            reset, so write it down somewhere safe.
          </div>
        )}
        {error && <div className="gate__error">{error}</div>}
        <Button type="submit" variant="primary" size="lg" block loading={busy}>
          {mode === 'create' ? 'Create account' : 'Sign in'}
        </Button>
        <LegalLinks />
      </form>
    </div>
  );
}

export function LockScreen({ notice }: { notice?: string } = {}) {
  const { account, hasPin } = useSession();
  const [usePin, setUsePin] = useState(hasPin);
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tryPin = async (value: string) => {
    setBusy(true);
    setError(null);
    try {
      await unlockWithPin(value);
    } catch (err) {
      setError((err as Error).message);
      setPin('');
    } finally {
      setBusy(false);
    }
  };

  const press = (d: string) => {
    if (busy) return;
    const next = (pin + d).slice(0, 8);
    setPin(next);
    if (
      next.length >= 4 &&
      next.length ===
        (localStorage.getItem('ember.pinLength')
          ? Number(localStorage.getItem('ember.pinLength'))
          : 6)
    )
      void tryPin(next);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (usePin) await unlockWithPin(pin);
      else await unlock(password, remember);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="gate">
      <form className="gate__card" onSubmit={submit}>
        <div className="gate__logo" />
        <div className="gate__title">{APP_NAME} is locked</div>
        <div className="gate__sub">{account?.email}</div>
        {notice && <div className="gate__note">{notice}</div>}
        {usePin ? (
          <>
            <div className="pindots">
              {Array.from({ length: Math.max(4, pin.length) }, (_, i) => (
                <span key={i} className={i < pin.length ? 'on' : ''} />
              ))}
            </div>
            <div className="pinpad">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
                <button key={d} type="button" onClick={() => press(d)}>
                  {d}
                </button>
              ))}
              <button type="button" onClick={() => setPin('')} aria-label="Clear">
                C
              </button>
              <button type="button" onClick={() => press('0')}>
                0
              </button>
              <button type="button" onClick={() => setPin(pin.slice(0, -1))} aria-label="Delete">
                <Backspace size={24} />
              </button>
            </div>
            <Button
              type="submit"
              variant="primary"
              size="lg"
              block
              loading={busy}
              disabled={pin.length < 4}
            >
              Unlock
            </Button>
            <Button variant="ghost" onClick={() => setUsePin(false)}>
              Use password instead
            </Button>
          </>
        ) : (
          <>
            <Input
              type="password"
              autoFocus
              autoComplete="current-password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <label className="row between">
              <span>Keep this device unlocked</span>
              <Toggle checked={remember} onChange={setRemember} label="Remember" />
            </label>
            <Button type="submit" variant="primary" size="lg" block loading={busy}>
              Unlock
            </Button>
            {hasPin && (
              <Button variant="ghost" onClick={() => setUsePin(true)}>
                Use PIN
              </Button>
            )}
          </>
        )}
        {error && <div className="gate__error">{error}</div>}
        <Button
          variant="ghost"
          size="sm"
          onClick={async () => {
            if (
              await confirmDialog({
                title: 'Sign out and wipe this device?',
                message:
                  'Removes all local data from this device. Anything not yet synced is lost. Your synced data stays on the server.',
                confirmText: 'Wipe this device',
                danger: true,
              })
            )
              await signOut();
          }}
        >
          Forgot password? Sign out of this device
        </Button>
      </form>
    </div>
  );
}

export function Splash() {
  return (
    <div className="splash">
      <div className="gate__logo" />
    </div>
  );
}

/** Privacy Policy and Terms, on the live site (the Android app bundles no copy). */
function LegalLinks() {
  const base = isBundled ? 'https://circahealth.app' : '';
  return (
    <div className="gate__legal">
      <a href={`${base}/privacy`} target="_blank" rel="noreferrer">
        Privacy Policy
      </a>
      {' · '}
      <a href={`${base}/terms`} target="_blank" rel="noreferrer">
        Terms of Service
      </a>
    </div>
  );
}
